import type {
  AggregationType,
  EntityStatus,
} from '@monorepo/shared/schemas/common'
import {
  AGGREGATION_TYPE,
  AGGREGATION_TYPES,
  DEFAULT_UNIT_LABEL,
  ENTITY_STATUS,
} from '@monorepo/shared/schemas/common'
import type {
  FeatureResponse,
  UpdateFeature,
} from '@monorepo/shared/schemas/features'
import { useNavigate } from '@tanstack/react-router'
import { DollarSign, Loader2, Save, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Separator } from '@/shared/components/ui/separator'
import { Textarea } from '@/shared/components/ui/textarea'
import { useCreateFeature, useUpdateFeature } from '../hooks/useFeatures'
import { CostSignalSelector } from './CostSignalSelector'

interface FeatureDrawerProps {
  feature?: FeatureResponse
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function FeatureDrawer({
  feature,
  open,
  onOpenChange,
}: FeatureDrawerProps) {
  const navigate = useNavigate()
  const isEditing = !!feature

  const [formData, setFormData] = useState<UpdateFeature>({
    feature_name: '',
    description: '',
    external_id: '',
    status: ENTITY_STATUS.draft,
  })

  const [selectedCostSignalIds, setSelectedCostSignalIds] = useState<string[]>(
    []
  )
  const [revenuePerUnit, setRevenuePerUnit] = useState<number>(0)

  // Billable signal fields
  const [signalName, setSignalName] = useState<string>('')
  const [eventName, setEventName] = useState<string>('')
  const [aggregation, setAggregation] = useState<AggregationType>(
    AGGREGATION_TYPE.count
  )
  const [unitLabel, setUnitLabel] = useState<string>('')

  const createFeature = useCreateFeature()
  const updateFeature = useUpdateFeature()

  // Update form data when feature changes or drawer opens
  useEffect(() => {
    if (feature && open) {
      // Edit mode: populate form with existing data
      setFormData({
        feature_name: feature.feature_name,
        description: feature.description || '',
        external_id: feature.external_id,
        status: feature.status,
      })
      // Initialize cost signal IDs from feature
      setSelectedCostSignalIds(
        feature.cost_signals?.map(cs => cs.signal_id) || []
      )
      // Initialize revenue metrics
      setRevenuePerUnit(feature.value?.revenue_per_unit || 0)

      // Initialize billable signal fields
      if (feature.billable_signal) {
        setSignalName(feature.billable_signal.signal_name)
        setEventName(feature.billable_signal.signal_code)
        setAggregation(feature.billable_signal.aggregation_type)
        setUnitLabel(feature.billable_signal.unit_label)
      } else {
        // Reset to empty if no billable signal
        setSignalName('')
        setEventName('')
        setAggregation(AGGREGATION_TYPE.count)
        setUnitLabel('')
      }
    } else if (!feature && open) {
      // Create mode: reset form to empty defaults
      setFormData({
        feature_name: '',
        description: '',
        external_id: '',
        status: ENTITY_STATUS.draft,
      })
      setSelectedCostSignalIds([])
      setRevenuePerUnit(0)
      setSignalName('')
      setEventName('')
      setAggregation(AGGREGATION_TYPE.count)
      setUnitLabel('')
    }
  }, [feature, open])

  const handleSave = async () => {
    try {
      if (isEditing) {
        // Build billable signal for update - must include signal_id if exists
        let updateBillableSignal:
          | {
              signal_id?: string
              signal_name: string
              signal_code: string
              aggregation_type: AggregationType
              unit_label: string
            }
          | undefined
        if (signalName && eventName) {
          if (feature?.billable_signal?.signal_id) {
            // Preserve existing signal_id when editing
            updateBillableSignal = {
              signal_id: feature.billable_signal.signal_id,
              signal_name: signalName,
              signal_code: eventName,
              aggregation_type: aggregation,
              unit_label: unitLabel ?? DEFAULT_UNIT_LABEL,
            }
          } else {
            // New signal being added to existing feature (no signal_id yet)
            updateBillableSignal = {
              signal_name: signalName,
              signal_code: eventName,
              aggregation_type: aggregation,
              unit_label: unitLabel ?? DEFAULT_UNIT_LABEL,
            }
          }
        }

        // Update existing feature
        await updateFeature.mutateAsync({
          id: feature.id,
          external_id: formData.external_id,
          feature_name: formData.feature_name,
          description: formData.description || undefined,
          status: formData.status,
          billable_signal: updateBillableSignal,
          cost_signal_ids:
            selectedCostSignalIds.length > 0
              ? selectedCostSignalIds
              : undefined,
          value: revenuePerUnit
            ? {
                revenue_per_unit: revenuePerUnit,
              }
            : undefined,
        })
        onOpenChange(false)
      } else {
        // Create new feature - required fields are validated by canSave
        if (
          !formData.external_id ||
          !formData.feature_name ||
          !formData.status
        ) {
          return // Should never happen due to canSave validation
        }

        // Build billable signal for creation - no signal_id needed
        let createBillableSignal:
          | {
              signal_name: string
              signal_code: string
              aggregation_type: AggregationType
              unit_label: string
            }
          | undefined
        if (signalName && eventName) {
          createBillableSignal = {
            signal_name: signalName,
            signal_code: eventName,
            aggregation_type: aggregation,
            unit_label: unitLabel ?? DEFAULT_UNIT_LABEL,
          }
        }

        const newFeature = await createFeature.mutateAsync({
          external_id: formData.external_id,
          feature_name: formData.feature_name,
          description: formData.description || undefined,
          status: formData.status,
          billable_signal: createBillableSignal,
          cost_signal_ids:
            selectedCostSignalIds.length > 0
              ? selectedCostSignalIds
              : undefined,
          value: revenuePerUnit
            ? {
                revenue_per_unit: revenuePerUnit,
              }
            : undefined,
        })
        onOpenChange(false)
        // Navigate to the new feature's detail page
        navigate({
          to: '/features/$featureId',
          params: { featureId: newFeature.id },
        })
      }
    } catch {
      // Error toast is handled by the mutation hooks
    }
  }

  const handleCancel = () => {
    // Reset form data to original values if editing
    if (feature) {
      setFormData({
        feature_name: feature.feature_name,
        description: feature.description || '',
        external_id: feature.external_id,
        status: feature.status,
      })
      setSelectedCostSignalIds(
        feature.cost_signals?.map(cs => cs.signal_id) || []
      )
      setRevenuePerUnit(feature.value?.revenue_per_unit || 0)

      // Reset billable signal fields
      if (feature.billable_signal) {
        setSignalName(feature.billable_signal.signal_name)
        setEventName(feature.billable_signal.signal_code)
        setAggregation(feature.billable_signal.aggregation_type)
        setUnitLabel(feature.billable_signal.unit_label)
      }
    }
    onOpenChange(false)
  }

  const hasChanges =
    feature &&
    (formData.feature_name !== feature.feature_name ||
      formData.description !== (feature.description || '') ||
      formData.external_id !== feature.external_id ||
      formData.status !== feature.status ||
      JSON.stringify(selectedCostSignalIds.sort()) !==
        JSON.stringify(
          (feature.cost_signals?.map(cs => cs.signal_id) || []).sort()
        ) ||
      revenuePerUnit !== (feature.value?.revenue_per_unit || 0) ||
      // Check billable signal field changes
      (feature.billable_signal &&
        (signalName !== feature.billable_signal.signal_name ||
          eventName !== feature.billable_signal.signal_code ||
          aggregation !== feature.billable_signal.aggregation_type ||
          unitLabel !== feature.billable_signal.unit_label)))

  const isPending = createFeature.isPending || updateFeature.isPending
  const canSave = isEditing
    ? hasChanges &&
      formData.feature_name?.trim() &&
      formData.external_id?.trim()
    : formData.feature_name?.trim() && formData.external_id?.trim()

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction='right'>
      <DrawerContent className='h-full w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed right-0'>
        <DrawerHeader>
          <DrawerTitle>
            {isEditing ? 'Edit Feature' : 'Create New Feature'}
          </DrawerTitle>
          <DrawerDescription>
            {isEditing
              ? 'Update the feature information and settings.'
              : 'Create a new feature for your organization.'}
          </DrawerDescription>
        </DrawerHeader>

        <div className='flex-1 overflow-y-auto px-4'>
          <div className='space-y-4'>
            {/* Feature Name */}
            <div>
              <Label htmlFor='feature-name'>
                Feature Name <span className='text-red-500'>*</span>
              </Label>
              <Input
                id='feature-name'
                value={formData.feature_name}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    feature_name: e.target.value,
                  }))
                }
                placeholder='e.g., API Calls, Data Processing'
                className='mt-1'
                maxLength={100}
              />
            </div>

            {/* External ID */}
            <div>
              <Label htmlFor='external-id'>
                External ID <span className='text-red-500'>*</span>
              </Label>
              <Input
                id='external-id'
                value={formData.external_id}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    external_id: e.target.value,
                  }))
                }
                placeholder='e.g., api-calls, data-processing'
                className='mt-1'
                maxLength={100}
              />
              <p className='text-xs text-slate-500 mt-1'>
                Unique identifier for this feature (used in API calls)
              </p>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor='description'>Description</Label>
              <Textarea
                id='description'
                value={formData.description || ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder='Describe what this feature does...'
                rows={4}
                className='mt-1'
                maxLength={500}
              />
            </div>

            {/* Status */}
            <div>
              <Label htmlFor='status'>Status</Label>
              <Select
                value={formData.status}
                onValueChange={value =>
                  setFormData(prev => ({
                    ...prev,
                    status: value as EntityStatus,
                  }))
                }
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ENTITY_STATUS.active}>Active</SelectItem>
                  <SelectItem value={ENTITY_STATUS.draft}>Draft</SelectItem>
                  <SelectItem value={ENTITY_STATUS.inactive}>
                    Inactive
                  </SelectItem>
                  <SelectItem value={ENTITY_STATUS.archived}>
                    Archived
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className='text-xs text-slate-500 mt-1'>
                Draft features can be configured before activation
              </p>
            </div>

            {/* Signal Configuration */}
            <Separator className='my-6' />

            <div className='flex items-center justify-between'>
              <div>
                <h3 className='text-lg font-semibold'>
                  {isEditing
                    ? 'Billable Signal Configuration'
                    : 'Add Feature Signal'}
                </h3>
                <p className='text-sm text-slate-500 mt-1'>
                  Configure signals to track usage and generate revenue
                  (optional)
                </p>
              </div>
            </div>

            <div className='bg-slate-50 dark:bg-slate-900/50 border border-slate-200/60 dark:border-slate-800/60 rounded-lg p-6 space-y-6'>
              <h4 className='text-base font-semibold'>Signal Configuration</h4>

              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='signalName'>Signal Name *</Label>
                  <Input
                    id='signalName'
                    placeholder='e.g., Email Sent'
                    value={signalName}
                    onChange={e => setSignalName(e.target.value)}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='eventName'>Event Name *</Label>
                  <Input
                    id='eventName'
                    placeholder='e.g., email_sent'
                    value={eventName}
                    onChange={e => setEventName(e.target.value)}
                  />
                </div>
              </div>

              <div className='grid grid-cols-3 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='aggregation'>Aggregation</Label>
                  <Select
                    value={aggregation}
                    onValueChange={value =>
                      setAggregation(value as AggregationType)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AGGREGATION_TYPES.map(type => (
                        <SelectItem key={type} value={type}>
                          {type === 'unique_count'
                            ? 'Count Unique'
                            : type.charAt(0).toUpperCase() + type.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='unitLabel'>Unit Label</Label>
                  <Input
                    id='unitLabel'
                    placeholder='e.g., emails, tokens, API calls, minutes'
                    value={unitLabel}
                    onChange={e => setUnitLabel(e.target.value)}
                  />
                  <p className='text-xs text-slate-500 mt-1'>
                    Display name for what you're measuring
                  </p>
                </div>
                <div className='space-y-2'>
                  <Label
                    htmlFor='revenuePerUnit'
                    className='flex items-center gap-2'
                  >
                    <DollarSign className='w-4 h-4' />
                    Revenue per Unit ($)
                  </Label>
                  <Input
                    id='revenuePerUnit'
                    type='number'
                    step='0.01'
                    placeholder='0'
                    value={revenuePerUnit || ''}
                    onChange={e =>
                      setRevenuePerUnit(parseFloat(e.target.value) || 0)
                    }
                  />
                </div>
              </div>

              <CostSignalSelector
                selectedSignalIds={selectedCostSignalIds}
                onSelectionChange={setSelectedCostSignalIds}
              />
            </div>

            {/* Feature Info - Only show when editing */}
            {isEditing && feature && (
              <div className='pt-4 border-t'>
                <h4 className='font-medium text-sm text-slate-700 dark:text-slate-300 mb-3'>
                  Feature Information
                </h4>
                <div className='space-y-2 text-xs text-slate-500'>
                  <div>
                    <span className='font-medium'>ID:</span> {feature.id}
                  </div>
                  <div>
                    <span className='font-medium'>Created:</span>{' '}
                    {new Date(feature.created_at).toLocaleDateString()}
                  </div>
                  <div>
                    <span className='font-medium'>Updated:</span>{' '}
                    {new Date(feature.updated_at).toLocaleDateString()}
                  </div>
                  <div>
                    <span className='font-medium'>Billable Signal:</span>{' '}
                    {feature.billable_signal ? 'Yes' : 'No'}
                  </div>
                  <div>
                    <span className='font-medium'>Cost Signals:</span>{' '}
                    {feature.cost_signals?.length || 0}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DrawerFooter>
          <div className='flex gap-2 w-full'>
            <DrawerClose asChild>
              <Button
                variant='outline'
                onClick={handleCancel}
                disabled={isPending}
                className='flex-1'
              >
                <X className='w-4 h-4 mr-2' />
                Cancel
              </Button>
            </DrawerClose>
            <Button
              onClick={handleSave}
              disabled={isPending || !canSave}
              className='flex-1'
            >
              {isPending && <Loader2 className='w-4 h-4 mr-2 animate-spin' />}
              <Save className='w-4 h-4 mr-2' />
              {isEditing ? 'Save Changes' : 'Create Feature'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
